import classNames from "classnames";
import { ReactNode } from "react";
import NoDataFound from "shared/components/noDataFound";
import styles from "./style.module.scss";

interface BlockCardProps {
  /** Left-hand heading. Omit for a card that supplies its own header row. */
  title?: ReactNode;
  /** Rendered on the right of the header row (filters, toggles, selects). */
  actions?: ReactNode;
  /** Smaller heading used by the full-width summary blocks. */
  compactTitle?: boolean;
  /** Drops the body padding so tables and scroll lists can reach the edges. */
  flush?: boolean;
  /** Removes the header's bottom margin for blocks that space their own body. */
  headerTight?: boolean;
  className?: string;
  /** Lets a block override the header row's own layout. */
  headerClassName?: string;
  bodyClassName?: string;
  /**
   * True once loading finished and the block has nothing to show. The header
   * stays put so filters remain usable, only the body swaps for the empty panel.
   */
  empty?: boolean;
  children: ReactNode;
}

/**
 * The white rounded surface every analytics block sits on.
 * Keeps radius, shadow, padding and the header row consistent across blocks.
 */
function BlockCard({
  title,
  actions,
  compactTitle,
  flush,
  headerTight,
  className,
  headerClassName,
  bodyClassName,
  empty,
  children,
}: BlockCardProps) {
  const hasHeader = Boolean(title || actions);

  return (
    <section className={classNames(styles.card, flush && styles.flush, className)}>
      {hasHeader && (
        <div
          className={classNames(
            styles.header,
            flush && styles.headerFlush,
            headerTight && styles.headerTight,
            headerClassName
          )}
        >
          {title && (
            <div
              className={classNames(
                styles.title,
                compactTitle && styles.titleCompact
              )}
            >
              {title}
            </div>
          )}
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
      <div className={classNames(styles.body, !empty && bodyClassName)}>
        {empty ? <NoDataFound /> : children}
      </div>
    </section>
  );
}

export default BlockCard;
